import { Button, MuiMenuItem, TextField, tryParseJSONArray } from '@fox/atoms';
import { FlexColumnSmallSpaced } from '@fox/layout';
import styled from 'styled-components';
import { FormRenderMode, QuestionStyleTypes, useFormRuntimeContext, useQuestionStyle } from '../../../form-runtime-context';
import { QuestionBlockProps, QuestionChoice, QuestionDisplayType, SELECTALL_DISPLAY_TYPES } from '../contracts';
import { DropdownAnswer } from '../dropdown-answer';
import { ToggleButtons } from './toggle-buttons';
import { ToggleOption } from './toggle-option';

export const MenuItemStyled = styled(MuiMenuItem)`
	font-family: Roboto, sans-serif;
	font-size: 13px;
	font-weight: normal;
`;

export const ChoiceStyle = styled(TextField).attrs(
	() =>
		({
			size: 'small',
			variant: 'standard'
		}) as const
)`
	text-underline: none;
	font-weight: normal;
	.MuiInputBase-root {
		height: 100%;
	}
`;
export const MultiChoiceRoot = styled.div<{ $isRating?: boolean }>`
	padding-bottom: ${props => (props.$isRating ? '0px' : '20px')};
	${props => (props.$isRating ? 'display: flex; align-items: center; justify-content: center' : '')}
`;
export const ChoiceWrap = styled.div<{ $focused?: boolean }>`
	font-family: 'Roboto, sans-serif';
	font-size: 13px;
	color: '#333333';
	font-weight: 'normal';

	${props =>
		!props.$focused &&
		`.choiceDragHandle {
		opacity: 0;
		transition: all 0.2s ease-in-out;
	}
	.appearOnFocus {
		opacity: 0;
		transition: all 0.2s ease-in-out;
	}`}

	&:hover {
		.appearOnFocus {
			opacity: 1;
		}
		.choiceDragHandle {
			opacity: 1;
		}
	}
`;

const AnswerWrapper = styled.div<{ $isRating?: boolean; $orientation?: string }>`
	${props =>
		props.$orientation === 'horizontal'
			? `display: flex;
				width: 100%;
				flex-wrap: wrap;
				justify-content: flex-start;`
			: `display: flex;
	flex-direction: ${props.$isRating ? 'row' : 'column'};
	gap: ${props.$isRating ? '0px' : '10px'}; ${props.$isRating && 'justify-content: space-evenly; width: 100%;'}`}
`;

const MultiChoiceOptionWrapper = styled.div<{ $isRating: boolean }>`
	width: 100%;
	cursor: pointer;
	display: flex;

	${props => (props.$isRating ? 'flex-direction: column-reverse;' : 'gap: 4;')}
`;

export const RenderChoiceQuestion = (props: {
	id: string;
	allowMulti: boolean;
	allowOther: boolean;
	options: QuestionChoice[];
	fieldProps: QuestionBlockProps;
	isRating?: boolean;
	displayType?: QuestionDisplayType;
	orientation?: 'vertical' | 'horizontal';
	selectAll?: boolean;
}) => {
	const { options, allowMulti, allowOther, fieldProps, isRating, displayType, id, orientation, selectAll } = props;
	const questionName = fieldProps.name;
	const { formRenderMode, getAnswer, answerQuestion } = useFormRuntimeContext();

	const currentAnswer = getAnswer(questionName);
	const currentAnswerSelections = tryParseJSONArray<unknown>(currentAnswer?.answer?.answerJson, []);

	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const { fontSize: textAreaFontSize } = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const allSelected = currentAnswerSelections.filter(a => a !== '__other').length >= options.length;
	const currentValue: string | undefined = currentAnswer?.answer?.answerString;

	return (
		<MultiChoiceRoot $isRating={isRating}>
			<FlexColumnSmallSpaced>
				{allowMulti && selectAll && (displayType ? SELECTALL_DISPLAY_TYPES.includes(displayType) : false) ? (
					<Button
						size="small"
						onClick={() => {
							if (formRenderMode === FormRenderMode.design) return;
							if (!allSelected) {
								answerQuestion({
									questionName: questionName,
									answer: { answerJson: JSON.stringify(options.map(o => o.value)) }
								});
								return;
							}
							if (allSelected) {
								answerQuestion({
									questionName: questionName,
									answer: {}
								});
								return;
							}
						}}
						title={allSelected ? 'Unselect all' : 'Select all'}
						children={allSelected ? 'Unselect all' : 'Select all'}
						variant="secondary"
						color="inherit"
						style={{ alignSelf: 'start' }}
					/>
				) : null}
				<AnswerWrapper $isRating={isRating} $orientation={displayType !== 'dropdown' ? orientation : ''}>
					{displayType === 'dropdown' ? (
						<DropdownAnswer allowOther={allowOther} options={options} questionName={questionName} allowMulti={allowMulti} />
					) : displayType === 'toggle' ? (
						<ToggleOption options={options} orientation={orientation} currentValue={currentValue} id={id} fieldProps={fieldProps} />
					) : displayType === 'buttons' ? (
						<ToggleButtons options={options} orientation={orientation} currentValue={currentValue} id={id} fieldProps={fieldProps} />
					) : (
						options.map((o, i) => {
							const currentChecked =
								currentAnswerSelections.includes(o.displayName) === true || currentAnswer?.answer?.answerString === o.displayName;

							return (
								<div key={i}>
									<MultiChoiceOptionWrapper
										$isRating={!!isRating}
										onClick={c => {
											if (formRenderMode === FormRenderMode.design) {
												return;
											}
											const checked = !currentChecked;
											if (allowMulti) {
												let currentAnswers = tryParseJSONArray<unknown>(currentAnswer?.answer?.answerJson, []);
												if (checked) {
													currentAnswers.push(o.displayName);
												} else {
													currentAnswers = currentAnswers.filter(c => c !== o.displayName);
												}
												answerQuestion({
													questionName,
													answer: { answerJson: JSON.stringify(currentAnswers) }
												});
											} else {
												if (checked) {
													answerQuestion({
														questionName,
														answer: { answerString: o.displayName }
													});
												} else {
													answerQuestion({
														questionName,
														answer: {}
													});
												}
											}
										}}
									>
										<input
											onSelect={e => e.preventDefault()}
											style={{
												flex: 1,
												marginTop: 'auto',
												marginBottom: 'auto',
												maxWidth: 20,
												...(props.isRating ? { margin: 0 } : {})
											}}
											onChange={() => {}}
											checked={formRenderMode !== FormRenderMode.design ? currentChecked : false}
											type={displayType !== undefined ? displayType : allowMulti ? 'checkbox' : 'radio'}
										/>
										<div
											style={{
												flex: orientation === 'horizontal' ? 'initial' : 2,
												marginTop: 'auto',
												marginBottom: 'auto',
												textAlign: isRating ? 'center' : 'left'
											}}
										>
											<div
												style={{
													fontFamily,
													fontSize,
													color,
													textDecoration,
													fontStyle,
													margin: 0,
													fontWeight
												}}
											>
												{o.displayName}
											</div>
										</div>
									</MultiChoiceOptionWrapper>
								</div>
							);
						})
					)}

					{props.allowOther && displayType !== 'dropdown' && displayType !== 'toggle' ? (
						<div style={{}}>
							<div
								onClick={() => {
									if (formRenderMode === FormRenderMode.design) {
										return;
									}
									const checked = !(
										currentAnswerSelections.includes('__other') === true || currentAnswer?.answer?.answerString === '__other'
									);
									if (allowMulti) {
										let currentAnswers = tryParseJSONArray<unknown>(currentAnswer?.answer?.answerJson, []);
										if (checked) {
											currentAnswers.push('__other');
										} else {
											currentAnswers = currentAnswers.filter(c => c !== '__other');
										}
										answerQuestion({
											questionName,
											answer: { answerJson: JSON.stringify(currentAnswers) }
										});
									} else {
										if (checked) {
											answerQuestion({
												questionName,
												answer: { answerString: '__other' }
											});
										} else {
											answerQuestion({
												questionName,
												answer: {}
											});
										}
									}
								}}
								style={{ cursor: 'pointer', width: '100%', display: 'flex' }}
							>
								<input
									style={{
										flex: 1,
										maxWidth: 20,
										marginTop: 'auto',
										marginBottom: 'auto',
										...(props.isRating ? { margin: 0 } : {})
									}}
									onSelect={e => e.preventDefault()}
									onChange={() => {}}
									checked={
										(formRenderMode !== FormRenderMode.design && currentAnswerSelections.includes('__other') === true) ||
										currentAnswer?.answer?.answerString === '__other'
									}
									type={displayType !== undefined ? displayType : allowMulti ? 'checkbox' : 'radio'}
								/>{' '}
								<div style={{ marginTop: 'auto', marginBottom: 'auto', flex: 2 }}>
									<div
										style={{
											fontFamily,
											fontSize,
											color,
											textDecoration,
											fontStyle,
											margin: 0,
											fontWeight
										}}
									>
										Other
									</div>
								</div>
							</div>
						</div>
					) : null}
				</AnswerWrapper>
				{currentAnswerSelections.includes('__other') || currentAnswer?.answer?.answerString === '__other' ? (
					<TextField
						size="medium"
						multiline
						fullWidth
						minRows={3}
						sx={{
							'.MuiInputBase-root': {
								color,
								fontSize: textAreaFontSize,
								fontStyle,
								fontFamily,
								textDecoration,
								fontWeight
							},
							'input:focus, textarea': {
								border: '0',
								boxShadow: 'none'
							}
						}}
						value={currentAnswer?.answer?.answerOther}
						onChange={c => {
							if (formRenderMode === FormRenderMode.design) {
								return;
							}
							answerQuestion({
								questionName,
								answer: {
									...currentAnswer?.answer,
									answerOther: c.target.value
								}
							});
						}}
						placeholder="Details"
					/>
				) : null}
			</FlexColumnSmallSpaced>
		</MultiChoiceRoot>
	);
};
